<template>
    <div class="float-left mr-1">
        <b-button
            variant="warning"
            @click.stop="abrirModal()"
        >
            <i class="fa fa-exchange mr-2"></i> Remanejar Valores
        </b-button>

        <b-modal
            id="modalRemanejamentoValores"
            v-model="mostrarModal"
            size="lg"
            no-close-on-backdrop
            @show="resetForm()"
        >
            <div slot="modal-title">
                Remanejamento de Valores entre Itens
                <b-badge variant="warning">REDISTRIBUIÇÃO</b-badge>
            </div>

            <app-erros-box :erros="erros" v-if="erros.length > 0"></app-erros-box>

            <b-row>
                <b-col sm="12">
                    <b-form-group
                        :state="$v.itemOrigemId.$error ? false : null"
                        invalid-feedback="O item a remover é obrigatório"
                    >
                        <label for="itemOrigemId"><strong>Item a remover</strong></label>
                        <b-form-select
                            id="itemOrigemId"
                            v-model="itemOrigemId"
                            :options="opcoesItensOrigem"
                            text-field="text"
                            value-field="value"
                            @change="onOrigemChange()"
                            :state="$v.itemOrigemId.$error ? false : null"
                        >
                            <template v-slot:first>
                                <b-form-select-option :value="null" disabled>
                                    -- Selecione o item de origem --
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row v-if="itemOrigemId">
                <b-col sm="12">
                    <b-form-group>
                        <label for="valorDisponivel"><strong>Valor disponível</strong></label>
                        <b-form-input
                            id="valorDisponivel"
                            :value="valorDisponivelFormatado"
                            readonly
                            class="text-right font-weight-bold"
                            :class="saldoDisponivelClass"
                        ></b-form-input>
                        <small class="text-muted">Saldo previsto do item selecionado</small>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col sm="12">
                    <b-form-group
                        :state="$v.itemDestinoId.$error ? false : null"
                        invalid-feedback="O item a receber é obrigatório"
                    >
                        <label for="itemDestinoId"><strong>Item a receber</strong></label>
                        <b-form-select
                            id="itemDestinoId"
                            v-model="itemDestinoId"
                            :options="opcoesItensDestino"
                            text-field="text"
                            value-field="value"
                            :disabled="!itemOrigemId"
                            :state="$v.itemDestinoId.$error ? false : null"
                        >
                            <template v-slot:first>
                                <b-form-select-option :value="null" disabled>
                                    -- Selecione o item de destino --
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col sm="12">
                    <b-form-group
                        :state="$v.valorRemanejado.$error ? false : null"
                        :invalid-feedback="erroValor"
                    >
                        <label for="valorRemanejado"><strong>Valor a remanejar</strong></label>
                        <money
                            id="valorRemanejado"
                            v-model="valorRemanejado"
                            v-bind="money"
                            class="form-control text-right"
                            @input.native="$v.valorRemanejado.$touch()"
                        ></money>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col sm="12">
                    <b-form-group
                        :state="$v.justificativa.$error ? false : null"
                        invalid-feedback="A justificativa é obrigatória"
                    >
                        <label for="justificativa"><strong>Justificativa</strong> <span class="text-danger">*</span></label>
                        <b-form-textarea
                            id="justificativa"
                            v-model="justificativa"
                            :rows="4"
                            placeholder="Descreva o motivo do remanejamento..."
                            @input="$v.justificativa.$touch()"
                            :state="$v.justificativa.$error ? false : null"
                        ></b-form-textarea>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-alert show variant="info" class="mt-2">
                <i class="fa fa-info-circle mr-1"></i>
                O valor total do contrato não será alterado. A soma dos itens permanece idêntica após o remanejamento.
            </b-alert>

            <div slot="modal-footer">
                <b-button variant="secondary" @click.stop="fecharModal()">
                    Cancelar
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="desabilitarBtnSalvar"
                    @click.stop="confirmarSalvar()"
                    class="ml-1"
                >
                    <i class="fa fa-save mr-1"></i> Salvar
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { required, minValue } from 'vuelidate/lib/validators'
import ContratosServicos from './../ContratosServicos'

export default {
    props: {
        contrato: {
            type: Object,
            default: () => {}
        }
    },

    data() {
        return {
            mostrarModal: false,
            erros: [],
            itemOrigemId: null,
            itemDestinoId: null,
            valorRemanejado: 0,
            justificativa: '',
            money: {
                decimal: ',',
                thousands: '.',
                prefix: 'R$ ',
                suffix: '',
                precision: 2,
                masked: false
            },
            salvoComSucesso: false
        }
    },

    computed: {
        itensPrincipaisAtivos() {
            if (!this.contrato || !this.contrato.itensContrato) return []
            return this.contrato.itensContrato.filter(item => item.status === 1)
        },

        opcoesItensOrigem() {
            return this.itensPrincipaisAtivos.map(item => ({
                value: item.itemContratoId,
                text: `Nº ${item.ordem} - ${item.descricao} (Saldo: ${this.formatarMoeda(item.saldoValor)})`
            }))
        },

        opcoesItensDestino() {
            return this.itensPrincipaisAtivos
                .filter(item => item.itemContratoId !== this.itemOrigemId)
                .map(item => ({
                    value: item.itemContratoId,
                    text: `Nº ${item.ordem} - ${item.descricao} (Saldo: ${this.formatarMoeda(item.saldoValor)})`
                }))
        },

        itemOrigem() {
            if (!this.itemOrigemId) return null
            return this.itensPrincipaisAtivos.find(i => i.itemContratoId === this.itemOrigemId) || null
        },

        saldoDisponivel() {
            return this.itemOrigem ? (this.itemOrigem.saldoValor || 0) : 0
        },

        valorDisponivelFormatado() {
            return this.formatarMoeda(this.saldoDisponivel)
        },

        saldoDisponivelClass() {
            if (this.saldoDisponivel <= 0) return 'text-danger'
            return 'text-success'
        },

        erroValor() {
            if (this.valorRemanejado <= 0) return 'O valor deve ser maior que zero'
            if (this.valorRemanejado > this.saldoDisponivel) {
                return `O valor não pode ser superior ao saldo disponível (${this.valorDisponivelFormatado})`
            }
            return 'O valor a remanejar é inválido'
        },

        desabilitarBtnSalvar() {
            return !this.justificativa || this.justificativa.trim() === '' || this.$v.$invalid
        }
    },

    methods: {
        abrirModal() {
            this.mostrarModal = true
        },

        fecharModal() {
            this.mostrarModal = false
            if (this.salvoComSucesso) {
                this.$emit('obterItensAtualizados')
            }
        },

        resetForm() {
            this.itemOrigemId = null
            this.itemDestinoId = null
            this.valorRemanejado = 0
            this.justificativa = ''
            this.erros = []
            this.salvoComSucesso = false
            this.$nextTick(() => {
                this.$v.$reset()
            })
        },

        onOrigemChange() {
            this.itemDestinoId = null
            this.valorRemanejado = 0
        },

        formatarMoeda(valor) {
            if (valor == null) return 'R$ 0,00'
            return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },

        confirmarSalvar() {
            this.$v.$touch()
            if (this.$v.$invalid) return

            const origem = this.itensPrincipaisAtivos.find(i => i.itemContratoId === this.itemOrigemId)
            const destino = this.itensPrincipaisAtivos.find(i => i.itemContratoId === this.itemDestinoId)

            this.$swal({
                title: 'Confirmar Remanejamento',
                html: `
                    <div style="text-align: left;">
                        <p><strong>Item de origem:</strong> ${origem ? origem.descricao : ''}</p>
                        <p><strong>Item de destino:</strong> ${destino ? destino.descricao : ''}</p>
                        <p><strong>Valor a remanejar:</strong> ${this.formatarMoeda(this.valorRemanejado)}</p>
                        <hr/>
                        <p class="text-warning"><i class="fa fa-exclamation-triangle"></i> Esta operação será sincronizada com o RM.</p>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirmar',
                cancelButtonText: 'Cancelar'
            }).then(result => {
                if (result.value) {
                    this.salvar()
                }
            })
        },

        salvar() {
            this.$store.dispatch('addRequest')
            const dto = {
                idContrato: this.$route.params.contratoId,
                itemOrigemId: this.itemOrigemId,
                itemDestinoId: this.itemDestinoId,
                valorRemanejado: this.valorRemanejado,
                justificativa: this.justificativa
            }

            ContratosServicos.remanejarValoresItemContrato(dto)
                .then(response => {
                    if (response) {
                        if (response.success) {
                            this.$toast.success(
                                'Remanejamento realizado com sucesso!',
                                'OK',
                                this.$toastConfig.success
                            )
                            this.salvoComSucesso = true
                            this.fecharModal()
                        } else {
                            this.erros = response.errors
                        }
                    }
                    this.$store.dispatch('removeRequest')
                })
        }
    },

    validations: {
        itemOrigemId: {
            required
        },
        itemDestinoId: {
            required
        },
        valorRemanejado: {
            required,
            minValue: minValue(0.01),
            naoUltrapassaSaldo(value) {
                return value <= this.saldoDisponivel
            }
        },
        justificativa: {
            required
        }
    }
}
</script>
